import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  User, Phone, Mail, MapPin, BookOpen, Upload, 
  CheckCircle, Loader2, ChevronRight, FileText, Image as ImageIcon
} from "lucide-react";
import { API } from "../App";

const EVIDENCE_TYPES = [
  { value: "bahi_bhat", label: "बही-भाट वंशावली" },
  { value: "tamrapatra", label: "ताम्रपत्र" },
  { value: "shilalekh", label: "शिलालेख" },
  { value: "ancient_statue", label: "प्राचीन प्रतिमा/चित्र" },
  { value: "oral_tradition", label: "बुजुर्गों की मौखिक परंपरा" },
  { value: "ancient_book", label: "प्राचीन पुस्तक" },
  { value: "other", label: "अन्य" },
];

const SECTIONS = [
  { id: 1, title: "योगदानकर्ता विवरण", titleEn: "Contributor Details" },
  { id: 2, title: "कुलदेवी विवरण", titleEn: "Kuldevi Details" },
  { id: 3, title: "साक्ष्य विवरण", titleEn: "Evidence Details" },
  { id: 4, title: "सहमति", titleEn: "Declaration" },
];

const KuldeviFormPage = () => {
  const [currentSection, setCurrentSection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    // Section 1: Contributor Details
    full_name: "",
    mobile_number: "",
    lf_id: "",
    email: "",
    current_residence: "",
    ancestral_village: "",
    // Section 2: Purohit Details (Optional)
    purohit_details: "",
    // Section 3: Kuldevi Details
    kuldevi_name: "",
    kuldevi_avatar: "",
    main_temple_location: "",
    traditions: "",
    // Section 4: Evidence Details
    evidence_type: "",
    evidence_period: "",
    evidence_custodian: "",
    evidence_description: "",
    evidence_files: [],
    // Section 5: Declaration
    consent_given: false,
  });

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newFiles = [];

    for (const file of files) {
      try {
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 10MB`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/public-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        newFiles.push({
          name: file.name,
          url: response.data.url,
          type: file.type
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploadedFiles(prev => [...prev, ...newFiles]);
    setForm(prev => ({
      ...prev,
      evidence_files: [...prev.evidence_files, ...newFiles.map(f => f.url)]
    }));
    setUploading(false);
    
    if (newFiles.length > 0) {
      toast.success(`${newFiles.length} file(s) uploaded successfully`);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setForm(prev => ({
      ...prev,
      evidence_files: prev.evidence_files.filter((_, i) => i !== index)
    }));
  };

  const validateSection = (section) => {
    if (section === 1) {
      return form.full_name.trim() && 
             form.mobile_number.trim() && 
             form.lf_id.trim() &&
             form.current_residence.trim() && 
             form.ancestral_village.trim();
    }
    if (section === 2) {
      return form.kuldevi_name.trim() && form.main_temple_location.trim();
    }
    if (section === 3) {
      return form.evidence_type && 
             form.evidence_custodian.trim() && 
             form.evidence_description.trim() &&
             form.evidence_files.length > 0;
    }
    if (section === 4) {
      return form.consent_given;
    }
    return false;
  };

  const handleNext = () => {
    if (!validateSection(currentSection)) {
      toast.error("कृपया सभी अनिवार्य फ़ील्ड भरें");
      return;
    }
    setCurrentSection(prev => prev + 1);
  };

  const handleSubmit = async () => {
    if (!validateSection(4)) {
      toast.error("कृपया सहमति प्रदान करें");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/kuldevi-form`, form);
      setSubmitted(true);
      toast.success("फॉर्म सफलतापूर्वक जमा हो गया!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "फॉर्म जमा करने में विफल");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">धन्यवाद!</h2>
            <p className="text-muted-foreground mb-4">
              आपकी जानकारी सफलतापूर्वक जमा हो गई है। कुलदेवी इतिहास संकलन में आपके योगदान के लिए आभार।
            </p>
            <Button onClick={() => window.location.href = '/'} className="w-full">
              होम पेज पर जाएं
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-2xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-orange-800" data-testid="form-title">
            कुलदेवी साक्ष्य संकलन फॉर्म
          </h1>
          <p className="text-orange-600 text-sm mt-1">Kuldevi Evidence Collection Form</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 mb-8 flex-wrap">
          {SECTIONS.map((s) => (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => {
                  if (s.id < currentSection) setCurrentSection(s.id);
                }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                  s.id === currentSection
                    ? "bg-orange-600 text-white"
                    : s.id < currentSection
                    ? "bg-green-100 text-green-700 cursor-pointer hover:bg-green-200"
                    : "bg-gray-100 text-gray-400"
                }`}
                data-testid={`step-${s.id}`}
              >
                {s.id < currentSection ? <CheckCircle className="w-3.5 h-3.5" /> : <span>{s.id}</span>}
                <span className="hidden sm:inline">{s.title}</span>
              </button>
              {s.id < 4 && <ChevronRight className="w-4 h-4 text-gray-300 mx-1" />}
            </div>
          ))}
        </div>

        {/* Section 1: Contributor Details */}
        {currentSection === 1 && (
          <Card className="border-orange-100" data-testid="section-1">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-semibold">१. योगदानकर्ता का विवरण</h2>
                <span className="text-sm text-muted-foreground">(Contributor Details)</span>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  पूरा नाम <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.full_name}
                  onChange={(e) => updateField("full_name", e.target.value)}
                  placeholder="अपना पूरा नाम लिखें"
                  data-testid="full-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  मोबाइल / व्हाट्सएप नंबर <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.mobile_number}
                  onChange={(e) => updateField("mobile_number", e.target.value)}
                  placeholder="मोबाइल नंबर"
                  type="tel"
                  data-testid="mobile-number"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  LF ID <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.lf_id}
                  onChange={(e) => updateField("lf_id", e.target.value)}
                  placeholder="आपकी LF ID"
                  data-testid="lf-id"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  ईमेल आईडी <span className="text-gray-400 text-xs">(ऐच्छिक)</span>
                </label>
                <Input
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="ईमेल आईडी"
                  type="email"
                  data-testid="email"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  वर्तमान निवास (शहर व राज्य) <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.current_residence}
                  onChange={(e) => updateField("current_residence", e.target.value)}
                  placeholder="जैसे: जयपुर, राजस्थान"
                  data-testid="current-residence"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  मूल पैतृक गाँव / स्थान <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.ancestral_village}
                  onChange={(e) => updateField("ancestral_village", e.target.value)}
                  placeholder="पैतृक गाँव का नाम"
                  data-testid="ancestral-village"
                />
              </div>

              {/* Section 2: Purohit Details (Optional) */}
              <div className="pt-4 border-t">
                <label className="text-sm font-medium mb-1 block">
                  २. कुल पुरोहित / भाट / बड़वा जी का विवरण <span className="text-gray-400 text-xs">(ऐच्छिक)</span>
                </label>
                <Textarea
                  value={form.purohit_details}
                  onChange={(e) => updateField("purohit_details", e.target.value)}
                  placeholder="यदि जानकारी हो तो लिखें..."
                  rows={3}
                  data-testid="purohit-details"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 2: Kuldevi Details */}
        {currentSection === 2 && (
          <Card className="border-orange-100" data-testid="section-2">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-semibold">३. कुलदेवी माताजी का विवरण</h2>
                <span className="text-sm text-muted-foreground">(Kuldevi Details)</span>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  कुलदेवी माताजी का प्रचलित नाम <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.kuldevi_name}
                  onChange={(e) => updateField("kuldevi_name", e.target.value)}
                  placeholder="माताजी का नाम"
                  data-testid="kuldevi-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  माताजी का स्वरूप / अवतार <span className="text-gray-400 text-xs">(ऐच्छिक)</span>
                </label>
                <Input
                  value={form.kuldevi_avatar}
                  onChange={(e) => updateField("kuldevi_avatar", e.target.value)}
                  placeholder="स्वरूप या अवतार का विवरण"
                  data-testid="kuldevi-avatar"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  मुख्य प्राचीन धाम / मंदिर का स्थान <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.main_temple_location}
                  onChange={(e) => updateField("main_temple_location", e.target.value)}
                  placeholder="मंदिर का स्थान व पता"
                  data-testid="temple-location"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  कुल परंपराएं व विशेष रीतियां <span className="text-gray-400 text-xs">(ऐच्छिक)</span>
                </label>
                <Textarea
                  value={form.traditions}
                  onChange={(e) => updateField("traditions", e.target.value)}
                  placeholder="भोग, जात-झड़ूला नियम आदि का विवरण..."
                  rows={3}
                  data-testid="traditions"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 3: Evidence Details */}
        {currentSection === 3 && (
          <Card className="border-orange-100" data-testid="section-3">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-semibold">४. साक्ष्य एवं प्रमाण का विवरण</h2>
                <span className="text-sm text-muted-foreground">(Evidence & Records)</span>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  साक्ष्य का प्रकार <span className="text-red-500">*</span>
                </label>
                <Select value={form.evidence_type} onValueChange={(v) => updateField("evidence_type", v)}>
                  <SelectTrigger data-testid="evidence-type">
                    <SelectValue placeholder="साक्ष्य का प्रकार चुनें" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVIDENCE_TYPES.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  प्रमाण का अनुमानित काल / समय <span className="text-gray-400 text-xs">(ऐच्छिक)</span>
                </label>
                <Input
                  value={form.evidence_period}
                  onChange={(e) => updateField("evidence_period", e.target.value)}
                  placeholder="संवत, वर्ष या पीढ़ी"
                  data-testid="evidence-period"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  मूल प्रमाण किसके पास सुरक्षित है? <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.evidence_custodian}
                  onChange={(e) => updateField("evidence_custodian", e.target.value)}
                  placeholder="व्यक्ति / संस्था का नाम व स्थान"
                  data-testid="evidence-custodian"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  साक्ष्य का विस्तृत विवरण <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={form.evidence_description}
                  onChange={(e) => updateField("evidence_description", e.target.value)}
                  placeholder="साक्ष्य का पूरा विवरण लिखें..."
                  rows={4}
                  data-testid="evidence-description"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  साक्ष्य दस्तावेज / फोटो अपलोड <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-orange-200 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="evidence-upload"
                    data-testid="evidence-upload"
                  />
                  <label 
                    htmlFor="evidence-upload" 
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 text-orange-500" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {uploading ? "अपलोड हो रहा है..." : "फोटो या दस्तावेज अपलोड करने के लिए क्लिक करें"}
                    </span>
                    <span className="text-xs text-gray-400">
                      (Images, PDF, DOC supported)
                    </span>
                  </label>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {file.type.startsWith('image/') ? (
                            <ImageIcon className="w-4 h-4 text-green-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-green-600" />
                          )}
                          <span className="text-sm text-green-700 truncate max-w-[200px]">{file.name}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeFile(idx)}
                          className="text-red-500 hover:text-red-700 h-6 px-2"
                        >
                          हटाएं
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 4: Declaration */}
        {currentSection === 4 && (
          <Card className="border-orange-100" data-testid="section-4">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-semibold">५. सहमति</h2>
                <span className="text-sm text-muted-foreground">(Declaration)</span>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    checked={form.consent_given}
                    onCheckedChange={(checked) => updateField("consent_given", checked)}
                    className="mt-1"
                    data-testid="consent-checkbox"
                  />
                  <label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">
                    <span className="font-medium">साक्ष्य संकलन, सत्यापन एवं इतिहास लेखन हेतु सहमति</span>
                    <span className="text-red-500"> *</span>
                    <p className="text-muted-foreground mt-2">
                      मैं इस बात की सहमति देता/देती हूं कि मेरे द्वारा प्रदान की गई जानकारी एवं साक्ष्य का उपयोग 
                      Lunawat Foundation द्वारा कुलदेवी इतिहास के संकलन, सत्यापन एवं लेखन के लिए किया जा सकता है।
                    </p>
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-3">जमा की जाने वाली जानकारी:</h3>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p>• योगदानकर्ता: {form.full_name}</p>
                  <p>• कुलदेवी: {form.kuldevi_name}</p>
                  <p>• मंदिर स्थान: {form.main_temple_location}</p>
                  <p>• साक्ष्य प्रकार: {EVIDENCE_TYPES.find(e => e.value === form.evidence_type)?.label}</p>
                  <p>• अपलोड फाइल्स: {uploadedFiles.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {currentSection > 1 ? (
            <Button variant="outline" onClick={() => setCurrentSection(prev => prev - 1)} data-testid="back-btn">
              पीछे जाएं
            </Button>
          ) : (
            <Button variant="outline" onClick={() => window.location.href = '/'}>
              होम पर जाएं
            </Button>
          )}

          {currentSection < 4 ? (
            <Button onClick={handleNext} className="bg-orange-600 hover:bg-orange-700" data-testid="next-btn">
              आगे बढ़ें <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !form.consent_given} className="bg-green-600 hover:bg-green-700" data-testid="submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              जमा करें
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default KuldeviFormPage;
